// frontend/src/pages/ProfilePage.tsx
// PRC-030: Mi perfil con las piezas del recorrido: datos personales y seguridad
// en dos bloques; el cambio de contraseña pide la actual, la nueva y su repetición.

import React, { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import axios from 'axios';
import { Box } from '@mui/material';

import axiosInstance from '../config/axiosInstance';
import PasswordField from '../modules/pricingJourney/components/PasswordField';
import { JourneyField, Notice, StatusBlock } from '../modules/pricingJourney/components/JourneyBlocks';
import { JourneyButton } from '../modules/pricingJourney/components/JourneyBasics';
import { focusFirstError, MIN_PASSWORD_LENGTH, OFFLINE_MESSAGE } from '../modules/pricingJourney/journeyAuth';
import { useAuth } from '../context/AuthContext';
import SettingsFrame, { SettingsBlock } from '../modules/pricingJourney/components/SettingsFrame';
import { SkeletonBlock } from '../modules/pricingJourney/components/Skeletons';

// --- TYPES ---
interface UserProfile {
    id: string;
    email: string;
    full_name: string;
    company_name: string;
    tier_id: string;
    stripe_customer_id: string;
}

interface PricingAppContext {
    pricing_role: string;
    account?: {
        account_name?: string | null;
    };
    profile?: {
        company_name?: string | null;
        tier_id?: string | null;
    };
    tier?: {
        id?: string | null;
        name?: string | null;
    };
}

interface ProfileUpdatePayload {
    full_name?: string;
    company_name?: string;
}

interface PasswordUpdatePayload {
    current_password: string;
    new_password: string;
}

type PasswordErrors = Partial<Record<'current' | 'next' | 'repeat', string>>;

// --- API SERVICES ---
const fetchUserProfile = async (): Promise<UserProfile> => {
    // Identidad personal del actor autenticado.
    const { data } = await axiosInstance.get('/auth/me');
    return data;
};

const fetchPricingAppContext = async (): Promise<PricingAppContext> => {
    // Contexto operativo compartido de la cuenta Pricing.
    const { data } = await axiosInstance.get('/auth/pricing-app-context');
    return data;
};

const updateUserProfile = async (profileData: ProfileUpdatePayload): Promise<unknown> => {
    const { data } = await axiosInstance.put('/auth/profile', profileData);
    return data;
};

const updateUserPassword = async (passwordData: PasswordUpdatePayload): Promise<unknown> => {
    const { data } = await axiosInstance.put('/auth/password', passwordData);
    return data;
};

const errorCode = (err: unknown): string | undefined => {
    if (!axios.isAxiosError(err)) return undefined;
    const detail = err.response?.data?.detail;
    return typeof detail === 'object' && detail ? detail.code : undefined;
};

// --- COMPONENTE ---
const ProfilePage: React.FC = () => {
    const queryClient = useQueryClient();
    const { enqueueSnackbar } = useSnackbar();

    const [fullName, setFullName] = useState('');
    const [nameError, setNameError] = useState<string | undefined>();
    const [currentPassword, setCurrentPassword] = useState('');
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [passwordErrors, setPasswordErrors] = useState<PasswordErrors>({});
    const [passwordFormError, setPasswordFormError] = useState<string | null>(null);
    const [currentUnlocked, setCurrentUnlocked] = useState(false);
    const { isPricingAdmin } = useAuth();

    const { data: profile, isLoading, error, refetch } = useQuery<UserProfile, Error>({
        queryKey: ['userProfile'],
        queryFn: fetchUserProfile,
        retry: 1,
    });

    const {
        data: pricingContext,
        isLoading: isPricingContextLoading,
        error: pricingContextError,
        refetch: refetchPricingContext,
    } = useQuery<PricingAppContext, Error>({
        queryKey: ['pricingAppContext'],
        queryFn: fetchPricingAppContext,
        retry: 1,
    });

    const companyName =
        pricingContext?.profile?.company_name ||
        pricingContext?.account?.account_name ||
        '';

    useEffect(() => {
        if (profile) {
            setFullName(profile.full_name || '');
        }
    }, [profile]);

    const profileMutation = useMutation({
        mutationFn: updateUserProfile,
        onSuccess: () => {
            enqueueSnackbar('Datos guardados.', { variant: 'success' });
            queryClient.invalidateQueries({ queryKey: ['userProfile'] });
        },
        onError: (err: unknown) => {
            enqueueSnackbar(axios.isAxiosError(err) && !err.response ? OFFLINE_MESSAGE : 'No hemos podido guardar los datos. Inténtalo de nuevo.', { variant: 'error' });
        },
    });

    const passwordMutation = useMutation({
        mutationFn: updateUserPassword,
        onSuccess: () => {
            enqueueSnackbar('Contraseña cambiada.', { variant: 'success' });
            setCurrentPassword('');
            setPassword('');
            setConfirmPassword('');
            setPasswordErrors({});
            setCurrentUnlocked(false);
        },
        onError: (err: unknown) => {
            const code = errorCode(err);
            if (code === 'CURRENT_PASSWORD_INVALID') {
                setPasswordErrors({ current: 'La contraseña actual no es correcta.' });
                document.getElementById('profile-current')?.focus();
            } else if (code === 'PASSWORD_CHECK_RATE_LIMITED') {
                setPasswordFormError('Has hecho demasiados intentos. Espera unos minutos y vuelve a probar.');
            } else if (axios.isAxiosError(err) && !err.response) {
                setPasswordFormError(OFFLINE_MESSAGE);
            } else {
                setPasswordFormError('No hemos podido cambiar la contraseña. Inténtalo de nuevo.');
            }
        },
    });

    const handleProfileSubmit = (event: React.FormEvent) => {
        event.preventDefault();
        if (!fullName.trim()) {
            setNameError('Escribe tu nombre y apellidos.');
            document.getElementById('profile-name')?.focus();
            return;
        }
        profileMutation.mutate({ full_name: fullName.trim() });
    };

    const passwordFieldError = (key: keyof PasswordErrors): string | undefined => {
        if (key === 'current') return currentPassword ? undefined : 'Escribe tu contraseña actual.';
        if (key === 'next') {
            if (password.length < MIN_PASSWORD_LENGTH) return `La contraseña necesita al menos ${MIN_PASSWORD_LENGTH} caracteres.`;
            return password === currentPassword ? 'La contraseña nueva tiene que ser distinta de la actual.' : undefined;
        }
        return confirmPassword && confirmPassword === password ? undefined : 'Las contraseñas no coinciden.';
    };

    const blurPassword = (key: keyof PasswordErrors, value: string) => () => {
        if (!value) return;
        setPasswordErrors((x) => ({ ...x, [key]: passwordFieldError(key) }));
    };

    const handlePasswordSubmit = (event: React.FormEvent) => {
        event.preventDefault();
        setPasswordFormError(null);
        const errors: PasswordErrors = {
            current: passwordFieldError('current'),
            next: passwordFieldError('next'),
            repeat: passwordFieldError('repeat'),
        };
        setPasswordErrors(errors);
        if (errors.current || errors.next || errors.repeat) {
            focusFirstError([[errors.current, 'profile-current'], [errors.next, 'profile-new'], [errors.repeat, 'profile-repeat']]);
            return;
        }
        passwordMutation.mutate({ current_password: currentPassword, new_password: password });
    };

    const roleLabel = isPricingAdmin ? 'Administrador' : 'Usuario';
    const resetName = () => { setFullName(profile?.full_name || ''); setNameError(undefined); };

    // PRC-036: plantilla de Ajustes (boceto G3).
    const page = (children: React.ReactNode) => (
        <SettingsFrame title="Mi perfil" context={[profile?.full_name, roleLabel].filter(Boolean).join(' · ')}>
            {children}
        </SettingsFrame>
    );

    if (isLoading || isPricingContextLoading) {
        return page(
            <>
                <SkeletonBlock h={330} radius="16px" />
                <SkeletonBlock h={360} radius="16px" />
            </>,
        );
    }

    if (error || pricingContextError) {
        return page(
            <StatusBlock
                variant="error"
                title="No hemos podido cargar tu perfil"
                text="Puede ser un problema de conexión. Vuelve a intentarlo."
                actions={<JourneyButton onClick={() => { void refetch(); void refetchPricingContext(); }}>Reintentar</JourneyButton>}
            />,
        );
    }

    return page(
        <>
            <Box component="form" noValidate onSubmit={handleProfileSubmit}>
                <SettingsBlock
                    id="datos"
                    title="Datos personales"
                    text={'Así te ven los demás usuarios' + (companyName ? ' de ' + companyName : '') + '.'}
                    footer={(
                        <>
                            <JourneyButton kind="ghost" small onClick={resetName} disabled={profileMutation.isPending || fullName === (profile?.full_name || '')}>Descartar</JourneyButton>
                            <JourneyButton type="submit" small disabled={profileMutation.isPending}>
                                {profileMutation.isPending ? 'Guardando…' : 'Guardar cambios'}
                            </JourneyButton>
                        </>
                    )}
                >
                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' }, gap: 2 }}>
                        <JourneyField
                            id="profile-name" label="Nombre" value={fullName} autoComplete="name"
                            error={nameError}
                            help="Aparece en el menú y en el historial."
                            onChange={(e) => { setFullName(e.target.value); setNameError(undefined); }}
                            onBlur={() => setNameError(fullName.trim() ? undefined : 'Escribe tu nombre y apellidos.')}
                        />
                        <JourneyField
                            id="profile-email" label="Email" type="email" value={profile?.email ?? ''} autoComplete="email" readOnly
                            help="Con él inicias sesión. Para cambiarlo, escribe a soporte."
                            sx={{ color: 'rgba(248,250,252,.6)' }}
                        />
                        <JourneyField
                            id="profile-company" label="Empresa y rol" value={[companyName, roleLabel].filter(Boolean).join(' · ')} autoComplete="off" readOnly
                            help="Los gestiona el administrador."
                            sx={{ color: 'rgba(248,250,252,.6)' }}
                        />
                    </Box>
                </SettingsBlock>
            </Box>

            <Box component="form" noValidate onSubmit={handlePasswordSubmit} autoComplete="off">
                <SettingsBlock
                    id="seguridad"
                    title="Contraseña"
                    text="Para cambiarla te pedimos la actual. Nunca la mostramos."
                    footer={(
                        <JourneyButton type="submit" small disabled={passwordMutation.isPending}>
                            {passwordMutation.isPending ? 'Guardando…' : 'Cambiar contraseña'}
                        </JourneyButton>
                    )}
                >
                    {/* El gestor de contraseñas asocia la nueva a este email. */}
                    {profile?.email && <input type="email" name="username" autoComplete="username" value={profile.email} readOnly hidden />}
                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(3, minmax(0, 1fr))' }, gap: 2 }}>
                        {/* PRC-036: siempre vacía. Un campo de solo lectura no lo rellena el
                            navegador; se habilita al pulsarlo. */}
                        <PasswordField
                            id="profile-current" name="profile-current-password" label="Contraseña actual" value={currentPassword} autoComplete="off"
                            readOnly={!currentUnlocked}
                            onFocus={() => setCurrentUnlocked(true)}
                            help="Siempre vacía. El navegador no la rellena solo."
                            error={passwordErrors.current}
                            onChange={(e) => { setCurrentPassword(e.target.value); setPasswordErrors((x) => ({ ...x, current: undefined })); }}
                            onBlur={blurPassword('current', currentPassword)}
                        />
                        <PasswordField
                            id="profile-new" label="Nueva contraseña" value={password} autoComplete="new-password" showRequirements
                            error={passwordErrors.next}
                            onChange={(e) => { setPassword(e.target.value); setPasswordErrors((x) => ({ ...x, next: undefined })); }}
                            onBlur={blurPassword('next', password)}
                        />
                        <PasswordField
                            id="profile-repeat" label="Repite la nueva" value={confirmPassword} autoComplete="new-password"
                            help="Avisamos si no coinciden al salir del campo."
                            error={passwordErrors.repeat}
                            onChange={(e) => { setConfirmPassword(e.target.value); setPasswordErrors((x) => ({ ...x, repeat: undefined })); }}
                            onBlur={blurPassword('repeat', confirmPassword)}
                        />
                    </Box>
                    {passwordFormError && <Notice tone="danger">{passwordFormError}</Notice>}
                </SettingsBlock>
            </Box>
        </>,
    );
};

export default ProfilePage;
